import { NavLink } from 'react-router-dom'
import { useAccount } from '@/entities/session'
import { useClientEvents } from '@/entities/client-event'
import { useLogout } from '@/features/auth'
import { BotSelect } from '@/entities/bot'
import styles from './Sidebar.module.css'

interface SidebarProps {
  open: boolean
  onNavigate: () => void
}

/**
 * Основные рабочие разделы кабинета. Настройки собраны в один пункт,
 * а клиентские изменения вынесены отдельно: у них свой счётчик и ритм.
 */
const ITEMS = [
  { to: '/', label: 'Обзор', icon: OverviewIcon, end: true },
  { to: '/client-events', label: 'Что нового', icon: UpdatesIcon, end: false },
  { to: '/onboarding', label: 'Настройка', icon: WandIcon, end: false },
  { to: '/test-call', label: 'Тестовый звонок', icon: PhoneIcon, end: false },
  { to: '/campaigns', label: 'Обзвоны', icon: CampaignIcon, end: false },
  { to: '/calls', label: 'Звонки', icon: ListIcon, end: false },
  { to: '/agenda', label: 'Что сделать', icon: AgendaIcon, end: false },
  { to: '/settings', label: 'Настройка агента', icon: SettingsIcon, end: false },
  { to: '/telephony', label: 'Телефония · SIP', icon: SettingsIcon, end: false },
  { to: '/account', label: 'Аккаунт и пароль', icon: SettingsIcon, end: false },
]

const INTEGRATIONS_ITEM = { to: '/integrations', label: 'Интеграции', icon: PlugIcon, end: false }
const ADMIN_ITEM = { to: '/admin', label: 'Админка', icon: AdminIcon, end: false }

export function Sidebar({ open, onNavigate }: SidebarProps) {
  const { data: account } = useAccount()
  const unreadEvents = useClientEvents({ limit: 1 }, 30_000)
  const logout = useLogout()
  const unread = unreadEvents.data?.unread ?? 0

  return (
    <aside className={`${styles.sidebar} ${open ? styles.open : ''}`}>
      <div className={styles.logo}>
        <span className={styles.mark}>
          <LogoIcon />
        </span>
        <span>
          синта<span className={styles.logoSoft}>.бот</span>
        </span>
      </div>

      <BotSelect companyId={account?.company.id} />

      <nav className={styles.nav}>
        {[...ITEMS, ...(account?.integrations ? [INTEGRATIONS_ITEM] : []), ...(account?.isSuperadmin ? [ADMIN_ITEM] : [])].map(
          ({ to, label, icon: Icon, end }) => {
            const showUnread = to === '/client-events' && unread > 0

            return (
              <NavLink
                key={to}
                to={to}
                end={end}
                // Закрываем выдвинутую колонку при переходе: на телефоне она
                // перекрывает страницу, ради которой её и открыли.
                onClick={onNavigate}
                className={({ isActive }) => `${styles.item} ${isActive ? styles.active : ''}`}
              >
                <Icon />
                <span className={styles.itemLabel}>{label}</span>
                {showUnread && (
                  <span
                    className={styles.badge}
                    aria-label={`Непрочитанных событий: ${unread}`}
                    title={`Непрочитанных событий: ${unread}`}
                  >
                    {unread > 99 ? '99+' : unread}
                  </span>
                )}
              </NavLink>
            )
          },
        )}
      </nav>

      <div className={styles.spacer} />

      <div className={styles.footer}>
        <span className={styles.company}>{account?.user.name || 'Вы'}</span>
        <span className={styles.email}>{account?.user.email ?? ''}</span>
        <button
          type="button"
          className={styles.logout}
          onClick={() => logout.mutate()}
          disabled={logout.isPending}
        >
          Выйти
        </button>
      </div>
    </aside>
  )
}

/* Иконки нарисованы здесь, а не подключены библиотекой: небольшой набор
   не оправдывает пакет на тысячи глифов и лишний вес в бандле. */

function LogoIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
      <path
        d="M2 7.5H3.2M5.1 4.3V10.7M7.5 1.9V13.1M9.9 4.3V10.7M12.8 7.5H14"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}


function WandIcon() {
  return (
    <svg className={styles.icon} width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M3 15L11.5 6.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M12.5 2.5L13.2 4.3L15 5L13.2 5.7L12.5 7.5L11.8 5.7L10 5L11.8 4.3L12.5 2.5Z" fill="currentColor" />
      <path d="M6 2L6.4 3.1L7.5 3.5L6.4 3.9L6 5L5.6 3.9L4.5 3.5L5.6 3.1L6 2Z" fill="currentColor" />
    </svg>
  )
}

function UpdatesIcon() {
  return (
    <svg className={styles.icon} width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M2 9H5L6.8 4.8L9.4 13.2L11.3 8H16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function AgendaIcon() {
  return (
    <svg className={styles.icon} width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2.5" y="3.5" width="13" height="12" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M6 1.8V4.6M12 1.8V4.6M2.5 7.2H15.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="6.4" cy="10.4" r="1" fill="currentColor" />
      <circle cx="9.6" cy="10.4" r="1" fill="currentColor" />
    </svg>
  )
}

function SettingsIcon() {
  return (
    <svg className={styles.icon} width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="2.6" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M9 1.8V3.4M9 14.6V16.2M16.2 9H14.6M3.4 9H1.8M14.1 3.9L13 5M5 13L3.9 14.1M14.1 14.1L13 13M5 5L3.9 3.9"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}

function OverviewIcon() {
  return (
    <svg className={styles.icon} width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2" y="2" width="6" height="6" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <rect x="10" y="2" width="6" height="6" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <rect x="2" y="10" width="6" height="6" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <rect x="10" y="10" width="6" height="6" rx="2" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  )
}

function AdminIcon() {
  return (
    <svg className={styles.icon} width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M9 2.2L14.5 4.4V8.5C14.5 11.8 12.3 14.7 9 15.8C5.7 14.7 3.5 11.8 3.5 8.5V4.4L9 2.2Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M6.7 8.8L8.2 10.3L11.5 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function PhoneIcon() {
  return (
    <svg className={styles.icon} width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M6.4 2.4L7.8 5.7L6.3 7.2C7.1 8.9 9.1 10.9 10.8 11.7L12.3 10.2L15.6 11.6V14.5C15.6 15.1 15.1 15.6 14.5 15.6C7.7 15.2 2.8 10.3 2.4 3.5C2.4 2.9 2.9 2.4 3.5 2.4H6.4Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function ListIcon() {
  return (
    <svg className={styles.icon} width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M6.5 4.5H15M6.5 9H15M6.5 13.5H15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="3.2" cy="4.5" r="1.2" fill="currentColor" />
      <circle cx="3.2" cy="9" r="1.2" fill="currentColor" />
      <circle cx="3.2" cy="13.5" r="1.2" fill="currentColor" />
    </svg>
  )
}



function PlugIcon() {
  return (
    <svg className={styles.icon} width="18" height="18" viewBox="0 0 20 20" fill="none">
      <path d="M7.5 3V6.5M12.5 3V6.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path
        d="M5 6.5H15V9C15 11.76 12.76 14 10 14C7.24 14 5 11.76 5 9V6.5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M10 14V17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

function CampaignIcon() {
  return (
    <svg className={styles.icon} width="18" height="18" viewBox="0 0 20 20" fill="none">
      <path
        d="M4 8.5V11.5C4 12.05 4.45 12.5 5 12.5H6.5L10.5 15.5V4.5L6.5 7.5H5C4.45 7.5 4 7.95 4 8.5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M13.5 7.5C14.3 8.2 14.8 9.05 14.8 10C14.8 10.95 14.3 11.8 13.5 12.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M15.5 5.5C16.8 6.6 17.5 8.25 17.5 10C17.5 11.75 16.8 13.4 15.5 14.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}
